/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { LegacyPortalAccountService } from "@spwn-portal/grpc";
import { useI18n } from "hooks/i18n/i18n";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { createCrossOriginPrivateConnectClient } from "utility/connectWeb";

import {
  RETURN_PATH_STORAGE_KEY,
  startggConfig,
  startggTicketPagePath,
} from "./startggConfig";
import { useStartggLink } from "./useStartggLink";

import type { FC } from "react";

// authorization code は1回限りで短命。再マウントで二重に交換して失敗するのを
// module スコープのガードで防ぐ
let processedCode: string | null = null;

const returnPath = (): string => {
  const saved = sessionStorage.getItem(RETURN_PATH_STORAGE_KEY);
  sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
  return saved ?? startggTicketPagePath();
};

/**
 * start.gg OAuth のコールバック。?code と ?state を受けて backend で交換する。
 * state の照合は backend が HttpOnly cookie と突き合わせて行うので、画面側は state を持たない。
 */
export const StartggCallback: FC = () => {
  const { t } = useI18n();
  const classes = styles();
  const history = useHistory();
  const location = useLocation();
  const client = useMemo(
    () => createCrossOriginPrivateConnectClient(LegacyPortalAccountService),
    []
  );
  const { save } = useStartggLink();
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = params.get("state");

    if (!code || !state) {
      setHasFailed(true);
      return;
    }
    if (processedCode === code) {
      return;
    }
    processedCode = code;

    void (async () => {
      try {
        const res = await client.exchangeStartggCode({
          code,
          redirectUri: startggConfig.redirectUri,
          state,
        });
        save({
          userId: res.startggUserId,
          username: res.startggUsername,
          linkedAt: Date.now(),
        });
        history.replace(returnPath());
      } catch (e) {
        console.error(e);
        setHasFailed(true);
      }
    })();
  }, [client, history, location.search, save]);

  return (
    <div css={classes.root}>
      <h1 css={classes.title}>{t("startggEntry.linkingTitle")}</h1>
      {hasFailed && (
        <Fragment>
          <p css={classes.message}>{t("startggEntry.linkingFailed")}</p>
          <button
            type="button"
            onClick={() => history.replace(returnPath())}
            css={classes.button}
          >
            {t("startggEntry.backButton")}
          </button>
        </Fragment>
      )}
    </div>
  );
};

const styles = () => {
  return {
    root: css({
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      maxWidth: "740px",
      margin: "0 auto",
      padding: "40px 16px",
    }),
    title: css({
      margin: 0,
      fontSize: "20px",
      fontWeight: "bold",
      lineHeight: "28px",
    }),
    message: css({
      margin: 0,
      fontSize: "14px",
      lineHeight: "20px",
    }),
    button: css({
      minHeight: "44px",
      padding: "10px 16px",
      color: "#0b0b0b",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      backgroundColor: "#fff",
      border: "1px solid #0b0b0b",
      cursor: "pointer",
    }),
  };
};
